import type { ChannelType } from "@workspace/api-client-react";

/**
 * The two physical output buses a venue drives. The engine guarantees at most
 * one active source per bus at any moment:
 *  - `audio`  — music / ads / announcements are mutually exclusive.
 *  - `screen` — the video channel owns the screen.
 */
export type Bus = "audio" | "screen";

export const AUDIO_CHANNELS: readonly ChannelType[] = [
  "music",
  "ads",
  "announcements",
];
export const SCREEN_CHANNELS: readonly ChannelType[] = ["video"];

/** Which bus a channel routes to. Only `video` lives on the screen bus. */
export function busForChannel(channelType: ChannelType): Bus {
  return channelType === "video" ? "screen" : "audio";
}

/** The active occupant of each bus (null = idle). */
export interface BusState {
  audio: ChannelType | null;
  screen: ChannelType | null;
}

export function emptyBusState(): BusState {
  return { audio: null, screen: null };
}

export interface BusStartResult {
  next: BusState;
  /** The channel that was displaced from the SAME bus, if any. */
  superseded: ChannelType | null;
}

/**
 * Start a channel on its bus. Returns the new state and which channel (if any)
 * was superseded — always the prior occupant of the *same* bus.
 */
export function applyBusStart(
  state: BusState,
  channelType: ChannelType,
): BusStartResult {
  if (busForChannel(channelType) === "audio") {
    const superseded =
      state.audio && state.audio !== channelType ? state.audio : null;
    return { next: { ...state, audio: channelType }, superseded };
  }
  const superseded =
    state.screen && state.screen !== channelType ? state.screen : null;
  return { next: { ...state, screen: channelType }, superseded };
}

/** Clear a channel from its bus, but only if it is the current occupant. */
export function applyBusStop(
  state: BusState,
  channelType: ChannelType,
): BusState {
  if (busForChannel(channelType) === "audio") {
    return state.audio === channelType ? { ...state, audio: null } : state;
  }
  return state.screen === channelType ? { ...state, screen: null } : state;
}

/** Which physical surfaces a channel's transport must drive. */
export interface SurfaceTargets {
  /** Drive the shared audio element (audio bus / full-AV sound). */
  audioElement: boolean;
  /** Drive the video element (screen bus picture). */
  videoElement: boolean;
}

export function transportTargets(
  channelType: ChannelType,
  audioElementChannel: ChannelType | null,
): SurfaceTargets {
  return {
    audioElement: audioElementChannel === channelType,
    videoElement: busForChannel(channelType) === "screen",
  };
}
